import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Users, AlertCircle, DollarSign, Cpu, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format, subDays, parseISO, startOfDay } from "date-fns";
import RegistrationGoalProgress from "@/components/dashboard/RegistrationGoalProgress";
import RegistrationsTimelineChart from "@/components/dashboard/RegistrationsTimelineChart";

interface AIUsageSummary {
  totalCost: number;
  totalRequests: number;
  totalTokens: number;
  providerCount: number;
}

interface Event {
  id: string;
  name: string;
  event_date: string | null;
  registration_goal: number | null;
}

interface Registration {
  created_at: string;
  ticket_count: number;
  has_ticket_conflict: boolean;
  reviewed_at: string | null;
  is_excluded: boolean;
}

interface Profile {
  created_at: string;
}

const Dashboard: React.FC = () => {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [libraryCount, setLibraryCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [aiUsage, setAIUsage] = useState<AIUsageSummary>({
    totalCost: 0,
    totalRequests: 0,
    totalTokens: 0,
    providerCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [aiUsageOpen, setAiUsageOpen] = useState(false);

  // Event-specific state
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [eventGoal, setEventGoal] = useState(300);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      loadStats();
      loadEvents();
    }

    const timeout = setTimeout(() => {
      setLoading(prevLoading => {
        if (prevLoading) {
          console.warn('Dashboard: Loading timeout reached, forcing completion');
          return false;
        }
        return prevLoading;
      });
    }, 15000);

    return () => clearTimeout(timeout);
  }, [organization?.id, orgLoading]);

  // Load registrations when event changes
  useEffect(() => {
    if (selectedEventId && organization?.id) {
      loadRegistrations(selectedEventId);
      loadProfiles();
    }
  }, [selectedEventId, organization?.id]);

  const loadEvents = async () => {
    if (!organization?.id) return;

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('events')
      .select('id, name, event_date, registration_goal')
      .eq('organization_id', organization.id)
      .gte('event_date', today) // Only future/current events
      .order('event_date', { ascending: true }); // Nearest upcoming first

    if (!error && data) {
      setEvents(data);
      // Select first event by default
      if (data.length > 0 && !selectedEventId) {
        setSelectedEventId(data[0].id);
        setEventGoal(data[0].registration_goal || 300);
      }
    }
  };

  const loadRegistrations = async (eventId: string) => {
    if (!organization?.id) return;

    const { data, error } = await supabase
      .from('event_registrations')
      .select('created_at, ticket_count, has_ticket_conflict, reviewed_at, is_excluded')
      .eq('organization_id', organization.id)
      .eq('event_id', eventId);

    if (!error && data) {
      setRegistrations(data);
    }

    // Also update the goal from the selected event
    const event = events.find(e => e.id === eventId);
    if (event) {
      setEventGoal(event.registration_goal || 300);
    }
  };

  const loadProfiles = async () => {
    if (!organization?.id) return;

    const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
    const { data, error } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('organization_id', organization.id)
      .gte('created_at', thirtyDaysAgo);

    if (!error && data) {
      setProfiles(data);
    }
  };

  const handleGoalUpdate = async (newGoal: number) => {
    if (!selectedEventId) return;

    const { error } = await supabase
      .from('events')
      .update({ registration_goal: newGoal })
      .eq('id', selectedEventId);

    if (error) {
      toast.error('Failed to update goal');
      return;
    }

    setEventGoal(newGoal);
    setEvents(prev => prev.map(e => 
      e.id === selectedEventId ? { ...e, registration_goal: newGoal } : e
    ));
    toast.success('Goal updated');
  };

  // Filter out excluded registrations for all calculations
  const activeRegistrations = useMemo(() => 
    registrations.filter(r => !r.is_excluded),
    [registrations]
  );

  // Calculate verified/flagged tickets (from active only)
  const { verifiedTickets, flaggedTickets } = useMemo(() => {
    const verified = activeRegistrations
      .filter(r => !r.has_ticket_conflict || r.reviewed_at)
      .reduce((sum, r) => sum + (r.ticket_count || 1), 0);
    
    const flagged = activeRegistrations
      .filter(r => r.has_ticket_conflict && !r.reviewed_at)
      .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

    return { verifiedTickets: verified, flaggedTickets: flagged };
  }, [activeRegistrations]);

  // Calculate timeline data (from active only)
  const timelineData = useMemo(() => {
    const days = 30;
    const data = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = startOfDay(subDays(new Date(), i));
      const dateStr = format(date, "yyyy-MM-dd");

      const dayRegs = activeRegistrations.filter(r =>
        format(parseISO(r.created_at), "yyyy-MM-dd") === dateStr
      );

      const verified = dayRegs
        .filter(r => !r.has_ticket_conflict || r.reviewed_at)
        .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

      const flagged = dayRegs
        .filter(r => r.has_ticket_conflict && !r.reviewed_at)
        .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

      const users = profiles.filter(p =>
        format(parseISO(p.created_at), "yyyy-MM-dd") === dateStr
      ).length;

      data.push({
        date: format(date, "MMM dd"),
        verified,
        flagged,
        users,
      });
    }

    return data;
  }, [activeRegistrations, profiles]);

  const loadStats = async () => {
    if (!organization?.id) return;
    
    try {
      const { count: libCount, error: libError } = await supabase
        .from('library_items')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organization.id);
      
      if (libError) {
        console.warn('Dashboard: Failed to load library count:', libError.message);
      } else {
        setLibraryCount(libCount || 0);
      }

      const { count: usrCount, error: usrError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organization.id);
      
      if (usrError) {
        console.warn('Dashboard: Failed to load user count:', usrError.message);
      } else {
        setUserCount(usrCount || 0);
      }

      const { count: pendCount, error: pendError } = await supabase
        .from('feature_requests_normalized')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organization.id)
        .eq('status', 'pending-review');
      
      if (pendError) {
        console.warn('Dashboard: Failed to load pending requests count:', pendError.message);
      } else {
        setPendingCount(pendCount || 0);
      }

      const { data: aiLogs, error: aiError } = await supabase
        .from('ai_usage_logs')
        .select('provider, total_tokens, estimated_cost')
        .eq('organization_id', organization.id);

      if (aiError) {
        console.warn('Dashboard: Failed to load AI usage stats:', aiError.message);
      } else if (aiLogs && aiLogs.length > 0) {
        const providers = new Set(aiLogs.map((l: any) => l.provider));
        const totalCost = aiLogs.reduce((sum: number, l: any) => sum + Number(l.estimated_cost), 0);
        const totalTokens = aiLogs.reduce((sum: number, l: any) => sum + l.total_tokens, 0);
        
        setAIUsage({
          totalCost,
          totalRequests: aiLogs.length,
          totalTokens,
          providerCount: providers.size,
        });
      }
    } catch (e) {
      console.warn('Dashboard: Unexpected error loading stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCost = (cost: number) => `$${cost.toFixed(4)}`;

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-display">Overview</h1>
        {events.length > 0 && (
          <Select value={selectedEventId || ""} onValueChange={setSelectedEventId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select event" />
            </SelectTrigger>
            <SelectContent>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Registration Goal Progress */}
      {selectedEventId && (
        <RegistrationGoalProgress
          verifiedTickets={verifiedTickets}
          flaggedTickets={flaggedTickets}
          goal={eventGoal}
          eventId={selectedEventId}
          onGoalUpdate={handleGoalUpdate}
          editable
        />
      )}

      {/* Registrations Timeline Chart */}
      {selectedEventId && (
        <RegistrationsTimelineChart data={timelineData} showUserSignups />
      )}
      
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card p-6 rounded-lg border border-border flex items-center gap-4">
          <div className="p-3 bg-primary/10 text-primary rounded-full">
            <FileText size={24} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Library Items</p>
            <p className="text-2xl font-bold">{libraryCount}</p>
          </div>
        </div>
        <div className="bg-card p-6 rounded-lg border border-border flex items-center gap-4">
          <div className="p-3 bg-secondary/10 text-secondary rounded-full">
            <Users size={24} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Registered Users</p>
            <p className="text-2xl font-bold">{userCount}</p>
          </div>
        </div>
        <div className="bg-card p-6 rounded-lg border border-border flex items-center gap-4">
          <div className="p-3 bg-amber-500/10 text-amber-500 rounded-full">
            <AlertCircle size={24} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Pending Requests</p>
            <p className="text-2xl font-bold">{pendingCount}</p>
          </div>
        </div>
      </div>

      {/* AI Usage Summary - Collapsible */}
      <Collapsible open={aiUsageOpen} onOpenChange={setAiUsageOpen}>
        <div className="flex items-center justify-between mb-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 p-0 h-auto hover:bg-transparent">
              <h2 className="text-lg font-semibold">AI Usage Summary</h2>
              <ChevronDown className={`h-4 w-4 transition-transform ${aiUsageOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <Link 
            to="/admin/analytics?tab=ai" 
            className="text-sm text-primary hover:underline"
          >
            View Details →
          </Link>
        </div>
        <CollapsibleContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card p-5 rounded-lg border border-primary/20 bg-primary/5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total AI Spend</p>
                <DollarSign size={18} className="text-primary" />
              </div>
              <p className="text-2xl font-bold mt-2">{formatCost(aiUsage.totalCost)}</p>
            </div>
            <div className="bg-card p-5 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Requests</p>
                <Activity size={18} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{aiUsage.totalRequests.toLocaleString()}</p>
            </div>
            <div className="bg-card p-5 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Tokens Used</p>
                <Cpu size={18} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{aiUsage.totalTokens.toLocaleString()}</p>
            </div>
            <div className="bg-card p-5 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">AI Providers</p>
                <Cpu size={18} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{aiUsage.providerCount}</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default Dashboard;
